/**
 * LoadImpactHero — the consolidated load impact hero, in the `.rd` design
 * language. Replaces BOTH the old `LoadGridSupplyPanel` (grid-supply harm) and
 * `LoadImpactSummaryCard` (net Health/Eco/Economic) — the two cards that
 * overlapped.
 *
 * Three endpoint cards (Human health / Ecosystem / Economic) show the NET
 * lifecycle number for the active lens, benefit (teal) vs net-burden (amber),
 * and — consequential-only — the sensitivity Floor/Central/Ceiling whisker.
 *
 * The hero states the ANSWER only. It used to also list the contributing blocks
 * (grid supply / NG damages / RE benefits / RE infra) under each endpoint, which
 * put the grid-electricity figure on screen a second time and invited exactly
 * the "is this double-counted?" question the lens unification exists to remove.
 * That decomposition now lives in `LoadFootprintWaterfall` — the single place
 * the grid number appears, and the only one that reconciles to the net.
 *
 * Purely presentational: the dashboard resolves the lens and passes the
 * lens-appropriate blocks in.
 */

import type { ImpactValues } from "../../load-portfolio/domain/types";
import type { EndpointMetric } from "../../impact/domain/scenarios";
import { toSigFigs, formatImpact } from "../../../utils/format";

export interface HeroWhisker {
  floorText: string;
  ceilingText: string;
  /** 0–100 position of central within the floor→ceiling span. */
  centralPct: number;
}

export interface LoadImpactHeroProps {
  net: ImpactValues;
  /** Drives the wording of the net-burden note; the figures live in the waterfall. */
  hasGridSupply: boolean;
  /** consequential-only sensitivity whiskers, keyed by endpoint metric. */
  whiskers?: Partial<Record<EndpointMetric, HeroWhisker>>;
  methodNote?: string;
}

const fmtUsd = (n: number) => formatImpact(n, "usd");

function Whisker({ w, benefit }: { w: HeroWhisker; benefit: boolean }) {
  const pct = Math.max(0, Math.min(100, w.centralPct));
  return (
    <div className="whisk">
      <div className="trk">
        <div className="fl" style={{ left: 0, width: `${pct}%` }} />
        <span className="cap" style={{ left: 0 }} />
        <span className="cap" style={{ right: 0 }} />
        <span className="cn" style={{ left: `${pct}%` }} />
      </div>
      <div className="sc">
        <div>
          <span className="t">{benefit ? "Floor" : "Ceiling"}</span>
          {w.floorText}
        </div>
        <div className="hi">
          <span className="t">{benefit ? "Ceiling" : "Floor"}</span>
          {w.ceilingText}
        </div>
      </div>
    </div>
  );
}

interface EndpointSpec {
  cls: "h" | "b" | "e";
  label: string;
  unit: string;
  metric: EndpointMetric;
  value: number;
  fmt: (n: number) => string;
}

export function LoadImpactHero(props: LoadImpactHeroProps) {
  const { net, hasGridSupply, whiskers, methodNote } = props;

  const specs: EndpointSpec[] = [
    {
      cls: "h",
      label: "Human health",
      unit: "DALYs",
      metric: "dalys",
      value: net.DALYs,
      fmt: (n) => toSigFigs(n),
    },
    {
      cls: "b",
      label: "Ecosystem",
      unit: "species·yr (PDF)",
      metric: "pdfs",
      value: net.PDFs,
      fmt: (n) => toSigFigs(n),
    },
    {
      cls: "e",
      label: "Economic",
      unit: "USD, ReCiPe monetized",
      metric: "usd",
      value: net.USD,
      fmt: fmtUsd,
    },
  ];

  // Loads are harm-signed: a positive endpoint (USD > 0 etc.) is a net BENEFIT
  // (teal); negative is a net burden (amber). Mirrors LoadImpactSummaryCard's
  // convention (isHealthHarm = DALYs < 0).
  const isNetBenefit = net.USD > 0;

  return (
    <div className="card">
      <div className="ch">
        <h2>Net lifecycle impact</h2>
        {methodNote && <span className="mut">{methodNote}</span>}
      </div>
      <div className="cb">
        <div className="grid3">
          {specs.map((s) => {
            const benefit = s.value >= 0;
            const w = whiskers?.[s.metric];
            return (
              <div
                key={s.cls}
                className={`ep ${s.cls} ${benefit ? "pos" : "neg"}`}
              >
                <div className="lab">
                  <span className="dot" />
                  {s.label}
                </div>
                <div className="big">
                  {s.metric === "usd"
                    ? s.fmt(s.value)
                    : `${s.value > 0 ? "+" : ""}${s.fmt(s.value)}`}
                </div>
                <div className="sub">
                  {benefit ? "net benefit" : "net burden"} (annual) · {s.unit}
                </div>

                {w && <Whisker w={w} benefit={benefit} />}
              </div>
            );
          })}
        </div>

        {!isNetBenefit && (
          <div className="lwarn">
            {hasGridSupply
              ? "This load carries a net environmental burden — the electricity it consumes causes more harm than any on-site RE offsets. More on-site / contracted renewables, or siting in a cleaner grid, reduces it."
              : "This load has a net environmental burden — on-site NG damages outweigh RE displacement benefits. Adding renewable capacity reduces the net impact."}
          </div>
        )}
      </div>
    </div>
  );
}
